// src/views/dashboard/money-flow/ClientDashboard.jsx
import React, { Component } from 'react';
import '../../../../styles/components/client-dashboard.scss';
import HeaderBanner from '../../../common/HeaderBanner';
import { SideBar } from '../../sidebar';
import i18n from '../../../../i18n';
import FilterSection from '../../../common/Filter';
import { selectStyles } from '../../../../styles/components/js/selectStyle';
import Select from 'react-select';
import DateRangeTags from '../../../common/DateRangeTags';
import { getGroupList } from '../Group/services/groupServices';
import { getAuctionPaymentDetails, getClients, getWinners } from './services/cashFlowService';
import { ArrowLeft, Download, FileMinus, FileSpreadsheet } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { TableSkeleton } from '../../../common/skelton/TableSkelton';
import DataTable from 'react-data-table-component';
import { tableStyles } from '../../../../styles/components/js/tableStyle';
import NoData from '../../../common/NoData';
import { fmt, formatDate } from '../../../../utils/common';
import { imageUrl } from '../../../../constants/global';
import { OMR } from '../../../common/icons';
import StatusBadge from '../../../common/StatusBadge';
import { TRANSACTION_STATUS } from '../../../../constants/status';
import NewDropdown from '../../../common/NewDropDown';

function detectTag(startDate, endDate) {
  if (!startDate || !endDate) return 'custom';
  const presets = ['7', '30', '90', '365'];
  for (const tag of presets) {
    const range = DateRangeTags.resolveRange(tag);
    if (range.startDate === startDate && range.endDate === endDate) return tag;
  }
  return 'custom';
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
class AuctionClientPaymentDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      openModal: null,
      isloading: false,
      hideSidebar: false,
      search: { startDate: '', endDate: '', client: null, group: [], bidder: null },
      clients: [],
      groups: [],
      bidders: [],
      clientLoading: false,
      groupLoading: false,
      bidderLoading: false,
      page: 1,
      limit: 15,
      dateTag: 'custom',
      auctionData: [],
      status: 'bidder_completed',
      auctionMeta: null,
      expandedRows: new Set(),
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(window.location.search);
    const urlStart = params.get('startDate');
    const urlEnd = params.get('endDate');

    let startDate, endDate, dateTag;

    if (urlStart && urlEnd) {
      // URL has explicit dates — restore them and detect which tag they match
      startDate = urlStart;
      endDate = urlEnd;
      dateTag = detectTag(urlStart, urlEnd);
    } else {
      // No URL dates — default to last 7 days
      dateTag = 'custom';
      // const range = DateRangeTags.resolveRange('7');
      // startDate = range.startDate;
      // endDate = range.endDate;
    }

    this.setState(
      {
        dateTag,
        search: {
          startDate,
          endDate,
          client: params.get('client') ? parseInt(params.get('client')) : null,

          group: params.get('group')
            ? params
                .get('group')
                .split(',')
                .map((id) => parseInt(id))
            : [],

          bidder: params.get('bidder') ? parseInt(params.get('bidder')) : null,
        },
        page: parseInt(params.get('page')) || 1,
        limit: parseInt(params.get('limit')) || 15,
        status: params.get('status') || 'bidder_completed',
      },
      () => {
        this.fetchClientList();
        this.fetchGroupList();
        this.fetchBidderList();
        this.fetchAuctionPaymentDetails();
      }
    );
  }

  // Update URL without refreshing page
  updateUrlQuery(q) {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(q).filter(([_, v]) => v !== '' && v !== null))
    ).toString();
    window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
  }

  async fetchAuctionPaymentDetails() {
    this.setState({ isloading: true });
    try {
      let { limit, page, status } = this.state;
      const { startDate, endDate, bidder, client, group } = this.state.search;
      const q = {
        page: page,
        limit: limit,
        status,
      };
      if (startDate) q.startDate = startDate;
      if (endDate) q.endDate = endDate;
      if (bidder) q.bidder = bidder;
      if (client) q.client = client;
      if (group?.length) q.group = group.join(',');

      this.updateUrlQuery(q);

      const response = await getAuctionPaymentDetails(q); // your API model fn
      if (response.success) {
        this.setState({
          auctionData: response.data,
          auctionMeta: response.meta?.totals,
          total: response.meta.total,
          isloading: false,
        });
      } else {
        this.setState({ isloading: false });
      }
    } catch {
      this.setState({ isloading: false });
    }
  }

  async fetchClientList() {
    this.setState({ clientLoading: true });
    try {
      const response = await getClients();
      if (response.success) {
        const clients = [
          {
            value: null,
            label: i18n.t('all') || 'All',
          },
          ...response.data.map((item) => ({
            value: item.id,
            label: item.organization_name,
          })),
        ];

        this.setState({
          clients,
          clientLoading: false,
        });
      } else {
        this.setState({ clientLoading: false });
      }
    } catch {
      this.setState({ clientLoading: false });
    }
  }

  async fetchGroupList() {
    this.setState({ groupLoading: true });
    const { client } = this.state.search;
    const q = client ? { organization: client } : {};
    const response = await getGroupList(q);
    if (response.success) {
      const groups = [
        {
          value: null,
          label: i18n.t('all') || 'All',
          organization: null,
        },
        ...response.data.map((item) => ({
          value: item.id,
          label: item.group_name,
          organization: item.organization,
        })),
      ];

      this.setState({
        groups,
        groupLoading: false,
      });
    } else {
      this.setState({ groupLoading: false });
    }
  }

  async fetchBidderList() {
    this.setState({ bidderLoading: true });
    const { client, group } = this.state.search;

    const q = {};
    if (client) q.client = client;
    if (group?.length) q.group = group.join(',');

    const response = await getWinners(q);
    if (response.success) {
      const bidders = [
        {
          value: null,
          label: i18n.t('all') || 'All',
        },
        ...response.data.map((item) => ({
          value: item.id,
          label: `${item.name} (${item.id})`,
        })),
      ];

      this.setState({
        bidders,
        bidderLoading: false,
      });
    } else {
      this.setState({ bidderLoading: false });
    }
  }

  handleDateTag = (tag, range) => {
    this.setState((prev) => ({
      dateTag: tag,
      search: { ...prev.search, ...range },
    }));
  };

  //  Client onChange — store as integer, clear groups, load group list
  handleClientChange = (selectedOption) => {
    const clientValue = selectedOption ? parseInt(selectedOption.value) : null;
    this.setState(
      (prevState) => ({
        search: {
          ...prevState.search,
          client: clientValue,
          group: [],
          bidder: null,
        },
        groups: [],
        bidders: [],
      }),
      async () => {
        await this.fetchGroupList();
        await this.fetchBidderList();
      }
    );
  };

  //  Group onChange — store as integer
  handleGroupChange = (selectedOptions) => {
    const selectedGroups = selectedOptions || [];

    // First selected group's organization becomes client
    const client = selectedGroups.length ? parseInt(selectedGroups[0].organization) : null;

    // Keep only groups from same organization
    const validGroups = selectedGroups.filter((item) => parseInt(item.organization) === client);

    const groupValues = validGroups.map((item) => parseInt(item.value));

    this.setState(
      (prevState) => ({
        search: {
          ...prevState.search,
          client,
          group: groupValues,
          bidder: null,
        },
        bidders: [],
      }),
      async () => {
        // Reload groups filtered by selected client
        await this.fetchGroupList();

        // Reload bidders
        await this.fetchBidderList();
      }
    );
  };

  //  Bidder onChange — store as integer
  handleBidderChange = (selectedOption) => {
    this.setState((prevState) => ({
      search: {
        ...prevState.search,
        bidder: selectedOption ? parseInt(selectedOption.value) : null,
      },
    }));
  };

  handleSearchChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      dateTag: 'custom',
      search: { ...prev.search, [name]: value },
    }));
  };
  handleSearch = () => {
    this.fetchAuctionPaymentDetails();
  };

  handleClear = () => {
    const range = DateRangeTags.resolveRange('7');
    this.setState(
      {
        dateTag: '7',
        search: {
          startDate: range.startDate,
          endDate: range.endDate,
          client: null,
          group: [],
          bidder: null,
        },
      },
      () => {
        this.fetchClientList();
        this.fetchGroupList();
        this.fetchBidderList();
        this.fetchAuctionPaymentDetails();
      }
    );
  };
  onChangePage = (page) => {
    this.setState({ page: page }, () => {
      this.fetchAuctionPaymentDetails();
    });
  };

  onChangeRowsPerPage = (limit) => {
    this.setState({ limit: limit, page: 1 }, () => {
      this.fetchAuctionPaymentDetails();
    });
  };

  // Calculate total paid from transactions
  calculateTotalPaid = (transactions) => {
    if (!transactions || !Array.isArray(transactions)) return 0;
    return transactions.reduce((sum, t) => sum + (t.paid_amount || 0), 0);
  };

  // Calculate pending amount
  calculatePendingAmount = (totalInvoice, totalPaid) => {
    return Math.max(0, totalInvoice - totalPaid);
  };

  // Main columns for the auction payments table
  getColumns = () => {
    return [
      {
        id: 1,
        name: 'Sl No',
        width: '70px',
        sortable: false,
        center: true,
        cell: (row, index) => (this.state.page - 1) * this.state.limit + index + 1,
      },
      {
        name: 'Client Name',
        width: '120px',
        cell: (row) => (
          <span className="highlight-table-value">{row.organization?.name || '-'}</span>
        ),
      },
      {
        name: 'Auction ID',
        selector: (row) => row.auction_id,
        width: '130px',
        sortable: true,
        cell: (row) => <span> {row.auction_id}</span>,
      },
      {
        name: i18n.t('auction_name'),
        minWidth: '140px',
        cell: (row) => `${row.title}`,
      },
      {
        name: i18n.t('group_name'),
        minWidth: '140px',
        cell: (row) => row.group.name,
        wrap: true,
      },
      {
        name: 'Bidder ID',
        selector: (row) => row.winner.id,
        width: '110px',
        sortable: true,
        cell: (row) => <span className="user-id">{row.winner.id}</span>,
      },
      {
        name: 'Name',
        selector: (row) => row.winner.id,
        width: '140px',
        sortable: true,
        cell: (row) => <span>{row.winner.name}</span>,
      },
      {
        name: i18n.t('email'),
        minWidth: '130px',
        cell: (row) => row.winner.email,
        wrap: true,
      },

      {
        name: i18n.t('bid_amount'),
        width: '140px',
        right: true,
        cell: (row) => row.financials.bid_amount,
      },
      {
        name: i18n.t('vat'),
        width: '140px',
        right: true,
        cell: (row) => row.financials.vat,
      },
      {
        name: i18n.t('service_charge'),
        width: '150px',
        right: true,
        cell: (row) => <span>{row.financials.service_charge}</span>,
      },
      {
        name: i18n.t('service_vat'),
        width: '150px',
        right: true,
        cell: (row) => <span>{row.financials.service_charge_vat}</span>,
      },
      {
        name: i18n.t('total_Invoice'),
        width: '150px',
        right: true,
        cell: (row) => <span className="highlight-table-value">{row.financials.total_amount}</span>,
      },
    ];
  };
  render() {
    const {
      clients,
      clientLoading,
      groups,
      groupLoading,
      bidders,
      bidderLoading,
      search,
      hideSidebar,
      auctionData,
      auctionMeta,
    } = this.state;
    const { startDate, endDate } = search;

    return (
      <React.Fragment>
        <HeaderBanner
          title={i18n.t('clinet_payment_cash_flow')}
          subtitle={i18n.t('clinet_payment_cash_flow_subtitle')}
        />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>
              <div
                className={
                  hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !hideSidebar })}
                      >
                        {hideSidebar ? (
                          <i className="icon-size-actual" />
                        ) : (
                          <i className="icon-size-fullscreen" />
                        )}
                      </span>
                    </div>
                    <div className="header-action">
                      {/* Back Button */}

                      <NavLink
                        to="/cash-flow/auction-payment"
                        className="button1 button1-gradient button1-back"
                      >
                        <ArrowLeft size={18} className="icon" />
                        <span>{i18n.t('back')}</span>
                      </NavLink>

                      <NewDropdown
                        label={i18n.t('export')}
                        // loading={exportLoading}
                        icon={Download}
                        items={[
                          {
                            label: i18n.t('excel'),
                            icon: FileSpreadsheet,
                            onClick: () => this.handleDownload('excel'),
                          },
                        ]}
                      />
                    </div>
                  </div>

                  <div className="card-body">
                    <DateRangeTags activeTag={this.state.dateTag} onChange={this.handleDateTag} />
                    <FilterSection
                      loading={this.state.isloading}
                      onSearch={this.handleSearch}
                      onClear={this.handleClear}
                    >
                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="startDate"
                          type="date"
                          name="startDate"
                          value={startDate}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" "
                        />
                        <label htmlFor="startDate" className="floating-label">
                          {i18n.t('start_date')}
                        </label>
                      </div>
                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="endDate"
                          type="date"
                          name="endDate"
                          value={endDate}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" "
                        />
                        <label htmlFor="endDate" className="floating-label">
                          {i18n.t('end_date')}
                        </label>
                      </div>
                      <div className="react-select-wrapper">
                        <Select
                          options={clients}
                          value={clients.find((c) => c.value === this.state.search.client) || null}
                          onChange={this.handleClientChange}
                          name="client"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={clientLoading ? `${i18n.t('loading')}...` : i18n.t('all')}
                          isLoading={clientLoading}
                          isDisabled={clientLoading}
                          isClearable
                        />
                        <label className="react-select-label">{i18n.t('client') || 'Client'}</label>
                      </div>

                      {/* Group Dropdown */}
                      <div className="react-select-wrapper">
                        <Select
                          options={groups}
                          //  compare number === number
                          value={groups.filter((c) => this.state.search.group?.includes(c.value))}
                          onChange={this.handleGroupChange}
                          name="group"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={groupLoading ? `${i18n.t('loading')}...` : i18n.t('all')}
                          isLoading={groupLoading}
                          isDisabled={groupLoading}
                          isMulti
                          isClearable
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                        />
                        <label className="react-select-label">{i18n.t('group_name')}</label>
                      </div>

                      {/* Bidder Dropdown */}
                      <div className="react-select-wrapper">
                        <Select
                          options={bidders}
                          //  compare number === number
                          value={bidders.find((c) => c.value === this.state.search.bidder) || null}
                          onChange={this.handleBidderChange}
                          name="bidder"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={bidderLoading ? `${i18n.t('loading')}...` : i18n.t('all')}
                          isLoading={bidderLoading}
                          isDisabled={bidderLoading}
                          isClearable
                        />
                        <label className="react-select-label">{i18n.t('bidder')}</label>
                      </div>
                    </FilterSection>

                    <div className="Data-Listing">
                      {this.state.isloading ? (
                        <TableSkeleton columns={6} rows={10} />
                      ) : auctionData.length ? (
                        <>
                          <DataTable
                            responsive={true}
                            columns={this.getColumns()}
                            data={auctionData}
                            customStyles={tableStyles}
                            highlightOnHover
                            pagination
                            paginationServer
                            paginationTotalRows={this.state.total}
                            paginationPerPage={this.state.limit}
                            paginationDefaultPage={this.state.page}
                            paginationRowsPerPageOptions={[15, 25, 50, 100]}
                            paginationComponentOptions={{
                              rowsPerPageText: i18n.t('rows_per_page'),
                              rangeSeparatorText: i18n.t('out'),
                            }}
                            onChangePage={this.onChangePage}
                            onChangeRowsPerPage={this.onChangeRowsPerPage}
                            expandableRows
                            expandableRowsComponent={({ data }) => (
                              <PaymentTable payments={data.payments} />
                            )}
                          />
                        </>
                      ) : (
                        <NoData message={i18n.t('no_data_available')} />
                      )}
                    </div>
                    <TotalSummaryRow totals={auctionMeta} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

export default AuctionClientPaymentDetails;

const PaymentTable = ({ payments }) => {
  if (!payments || payments.length === 0) {
    return (
      <div className="no-bidders">
        <div className="no-bidders-content">
          <span className="no-bidders-icon">
            <FileMinus />
          </span>

          <span>No Payments Available</span>
        </div>
      </div>
    );
  }

  return (
    <div className="receipts-wrapper payment-wrapper">
      <div className="receipts-header">
        <h6>Payment Information</h6>

        <span className="receipts-count">
          {payments.length} Payment{payments.length > 1 ? 's' : ''}
        </span>
      </div>

      <div className="receipts-table-container">
        <table className="receipts-table payment-table">
          <thead>
            <tr>
              <th>Payment ID</th>
              <th>Method</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Date</th>
              <th>Receipts</th>
            </tr>
          </thead>

          <tbody>
            {payments.map((payment) => (
              <React.Fragment key={payment.id}>
                <tr className="payment-row">
                  <td>
                    <span className="payment-id">{payment.id}</span>
                  </td>

                  <td>
                    <span className={`payment-method-badge ${payment.method}`}>
                      {payment.method}
                    </span>
                  </td>

                  <td>
                    <div className="amount-cell">{fmt(payment.credit)}</div>
                  </td>

                  <td>
                    <StatusBadge status={TRANSACTION_STATUS[payment.status]} />
                  </td>

                  <td>{formatDate(payment.date)}</td>

                  <td>
                    <span className="receipt-count-badge">{payment.receipts?.length || 0}</span>
                  </td>
                </tr>

                {/* RECEIPTS */}
                <tr className="nested-receipt-row">
                  <td colSpan="6">
                    <ReceiptTable receipts={payment.receipts} />
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ReceiptTable = ({ receipts }) => {
  if (!receipts || receipts.length === 0) {
    return (
      <div className="no-bidders mini-no-data">
        <div className="no-bidders-content">
          <span className="no-bidders-icon">
            <FileMinus />
          </span>

          <span>No Receipts Available</span>
        </div>
      </div>
    );
  }

  return (
    <div className="nested-receipts-wrapper">
      <table className="receipts-table nested-receipts-table">
        <thead>
          <tr>
            <th>Receipt Id</th>
            <th>Receipt Number</th>
            <th>Receipt Amount</th>
            <th>Payment Date</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {receipts.map((receipt) => (
            <tr key={receipt.id}>
              <td className="receipts-email">{receipt.id}</td>
              <td className="receipts-number">{receipt.receipt_number}</td>
              <td className="receipts-email">{receipt.amount}</td>
              <td className="receipts-number">{formatDate(receipt.created_at)}</td>
              <td className="receipts-number">
                <a
                  href={`${imageUrl}uploads/wallet/receipt/${receipt.receipt_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="receipt-link"
                >
                  {i18n.t('view')}
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── TOTAL SUMMARY ROW COMPONENT ─────────────────────────────────────
const TotalSummaryRow = ({ totals }) => {
  // ── SAFE DEFAULTS ─────────────────────────────
  const bidAmount = totals?.bid_amount || 0;
  const vat = totals?.vat || 0;
  const serviceCharge = totals?.service_charge || 0;
  const serviceVat = totals?.service_charge_vat || 0;

  const totalAmount = totals?.total_amount || 0;

  const clientAmount = totals?.client_amount || 0;
  const totalWithUs = totals?.mzadcom_amount || 0;

  return (
    <div className="auction-payment-summary-wrapper">
      {/* ── FIRST ROW ───────────────────── */}
      <div className="auction-payment-total-bar">
        <div className="total-item">
          <span>Total Bid Amount</span>
          <strong>
            {fmt(bidAmount)} <OMR size={12} />
          </strong>
        </div>

        <div className="total-item">
          <span>Total VAT</span>
          <strong>
            {fmt(vat)} <OMR size={12} />
          </strong>
        </div>

        <div className="total-item">
          <span>Total Service Charge</span>
          <strong>
            {fmt(serviceCharge)} <OMR size={12} />
          </strong>
        </div>

        <div className="total-item">
          <span>Total Service VAT</span>
          <strong>
            {fmt(serviceVat)} <OMR size={12} />
          </strong>
        </div>

        <div className="total-item grand-total">
          <span>Total Invoice Amount</span>
          <strong>
            {fmt(totalAmount)} <OMR size={12} color="#c2410c" />
          </strong>
        </div>
      </div>

      {/* ── SECOND ROW ───────────────────── */}
      <div className="auction-payment-total-bar secondary-row">
        <div className="total-item total-item-client">
          <span>Total Client Amount</span>
          <strong>
            {fmt(clientAmount)} <OMR size={12} color="#1d4ed8" />
          </strong>
          <small>Total Bid Amount + Total VAT</small>
        </div>

        <div className="total-item total-item-company">
          <span>Total For Mzadcom</span>
          <strong>
            {fmt(totalWithUs)} <OMR size={12} color="#15803d" />
          </strong>
          <small>Total Service Charge + Total Service VAT</small>
        </div>
      </div>
    </div>
  );
};
